/* eslint-disable react/no-unknown-property */
/* eslint-disable no-nested-ternary */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useLocale } from '@adsk/alloy-react-locale';
import { AccordionItem } from '@adsk/alloy-react-accordion';
import Toggle from '@adsk/alloy-react-toggle';
import Tooltip from '@adsk/alloy-react-tooltip';
import { PRODUCT_IDS, useProjectEntitlements } from '@adsk/alloy-provider';
import Notifications from '@adsk/alloy-react-notification';
import {
  AutoSpecsTooltip,
  useAdminFeatureFlag,
} from '@adsk/acs-admin-ui-components-shared-components';
import { FLAG_NAMES } from '@adsk/acs-admin-ui-components-helpers';
import { AlertInformationIcon } from '@adsk/alloy-react-icon';
import { PRODUCTS_ORDER } from '../../constants';
import ProductContent from '../ProductContent';
import ProductTools from '../ProductTools';
import {
  StyledAccordion,
  accordionItemContentStyle,
  accordionItemContainerStyle,
  toggleContainerStyle,
  toggleLabelStyle,
  noAccordionProductStyle,
  subscriptionNotificationStyle,
  productOptionTooltipContainer,
  productOptionTooltipIcon,
  emsProductStyle,
} from './styles';

const ProductsAccesses = ({
  isEmsAccount,
  onProductAccessChange,
  permissionsEditState,
  productsPermissions,
  selectedAccessLevel,
  setExpandedProduct,
  showProductsSectionRedesign,
}) => {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));
  const sortedProductsPermissions = productsPermissions.sort((a, b) => {
    return PRODUCTS_ORDER.indexOf(a.type) - PRODUCTS_ORDER.indexOf(b.type);
  });
  const { currentProject } = useProjectEntitlements();
  const isAccountAdmin = currentProject?.accessLevels?.accountAdministrator;

  const {
    ADMIN_FLAGS: { ACC_DISABLE_MANAGE_COST_ACCESS_FOR_PROJECT_ADMINS },
  } = FLAG_NAMES;

  const { value: disableCost } = useAdminFeatureFlag(
    ACC_DISABLE_MANAGE_COST_ACCESS_FOR_PROJECT_ADMINS,
    false
  );

  const renderToggleLabel = useCallback(
    (product) => {
      if (product.type === PRODUCT_IDS.INSIGHT) {
        return (
          <Tooltip
            content={
              product.on
                ? T.translate('INSIGHT_ON_TOOLTIP')
                : T.translate('INSIGHT_OFF_TOOLTIP')
            }
            shouldWrapChildren={false}
          >
            {product.on ? T.translate('TOGGLE_ON') : T.translate('TOGGLE_OFF')}
          </Tooltip>
        );
      }

      return product.on ? T.translate('TOGGLE_ON') : T.translate('TOGGLE_OFF');
    },
    [T]
  );

  return (
    <StyledAccordion>
      {sortedProductsPermissions.map((product) => (
        <div
          key={product.type}
          css={accordionItemContainerStyle}
          data-testid={`Product_${product.type}`}
        >
          <div css={toggleContainerStyle}>
            {!showProductsSectionRedesign && !product.disabled && (
              <Toggle
                checked={product.on}
                onChange={(value) => onProductAccessChange(product.type, value)}
                data-testid={`ProductsAccesses_Toggle_${product.type}`}
              />
            )}
            {showProductsSectionRedesign && (
              <Toggle
                checked={product.on}
                onChange={(value) => onProductAccessChange(product.type, value)}
                data-testid={`ProductsAccesses_Toggle_${product.type}`}
                disabled={
                  (disableCost &&
                    !isAccountAdmin &&
                    product.type === PRODUCT_IDS.COST) ||
                  product.disabled
                }
              />
            )}
            {!showProductsSectionRedesign && (
              <span
                css={toggleLabelStyle}
                data-testid={`ProductsAccesses_renderToggleLabel_span_${product.type}`}
              >
                {renderToggleLabel(product)}
              </span>
            )}
          </div>
          {product.on && !isEmsAccount ? (
            <AccordionItem
              className="ProjectMemberProfile_AccordionItem"
              data-testid={`ProductsAccesses_AccordionItem_${product.type}`}
              expanded={product.expanded}
              onExpand={(e) => {
                // validate that the href link of the tooltip is clickable
                if (e.target.id !== 'AutoSpecsToolTipHref') {
                  e.preventDefault();
                  e.stopPropagation();

                  setExpandedProduct(product.expanded ? null : product.type);
                }
              }}
              title={
                product.type === PRODUCT_IDS.AUTOSPECS ? (
                  <AutoSpecsTooltip
                    title={product.name}
                    message={T.translate('AUTOSPECS_TOOLTIP_TEXT', {
                      productName: 'Build',
                    })}
                    URLMessage={T.translate('LEARN_MORE')}
                  />
                ) : disableCost &&
                  !isAccountAdmin &&
                  product.type === PRODUCT_IDS.COST ? (
                  <>
                    <div style={productOptionTooltipContainer}>
                      {product.name}
                      <Tooltip
                        content={T.translate(
                          'EDIT_USER_TABLE_PRODUCT_COST_PRODUCT_TOOLTIP'
                        )}
                        data-testid="Cost_TooltipHeaderTooltip"
                      >
                        <AlertInformationIcon
                          data-testid="Cost_TooltipHeaderTooltipIcon"
                          size={16}
                          style={productOptionTooltipIcon}
                        />
                      </Tooltip>
                    </div>
                  </>
                ) : (
                  product.name
                )
              }
            >
              <div css={accordionItemContentStyle}>
                {showProductsSectionRedesign && product.subscriptionInfo && (
                  <Notifications
                    data-testid={`ProductsAccesses_Notifications_${product.type}`}
                    dismissible={false}
                    showIcon={false}
                    status={product.subscriptionInfo.status}
                    style={subscriptionNotificationStyle}
                  >
                    {product.subscriptionInfo.text}
                  </Notifications>
                )}
                {product.tools ? (
                  <ProductTools
                    data-testid={`ProductsAccesses_ProductTools_${product.type}`}
                    permissionsEditState={permissionsEditState}
                    tools={product.tools}
                  />
                ) : (
                  <ProductContent
                    productType={product.type}
                    selectedAccessLevel={selectedAccessLevel}
                  />
                )}
              </div>
            </AccordionItem>
          ) : (
            <div
              css={!isEmsAccount ? noAccordionProductStyle : emsProductStyle}
              data-testid={`ProductsAccesses_NoAccordionItem_${product.type}`}
            >
              {product.type === PRODUCT_IDS.AUTOSPECS ? (
                <AutoSpecsTooltip
                  title={product.name}
                  message={T.translate('AUTOSPECS_TOOLTIP_TEXT', {
                    productName: 'Build',
                  })}
                  URLMessage={T.translate('LEARN_MORE')}
                />
              ) : disableCost &&
                !isAccountAdmin &&
                product.type === PRODUCT_IDS.COST ? (
                <>
                  {product.name}
                  <div style={productOptionTooltipContainer}>
                    <Tooltip
                      content={T.translate(
                        'EDIT_USER_TABLE_PRODUCT_COST_PRODUCT_TOOLTIP'
                      )}
                      data-testid="Cost_TooltipHeaderTooltip"
                    >
                      <AlertInformationIcon
                        data-testid="Cost_TooltipHeaderTooltipIcon"
                        size={16}
                        style={productOptionTooltipIcon}
                      />
                    </Tooltip>
                  </div>
                </>
              ) : (
                product.name
              )}
            </div>
          )}
        </div>
      ))}
    </StyledAccordion>
  );
};

ProductsAccesses.propTypes = {
  isEmsAccount: PropTypes.bool.isRequired,
  onProductAccessChange: PropTypes.func.isRequired,
  permissionsEditState: PropTypes.bool.isRequired,
  productsPermissions: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedAccessLevel: PropTypes.string.isRequired,
  setExpandedProduct: PropTypes.func.isRequired,
  showProductsSectionRedesign: PropTypes.bool.isRequired,
};

export default ProductsAccesses;
